import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Button from "../../shared/Button";
import CustomInput from "../../shared/CustomInput";
import PasswordInput from "../../shared/PasswordInput";
import useAuth from "../../hooks/useAuth";
import { login } from "../../sdk/auth/auth";
import { useError } from "../../context/ErrorProvider";
import { useMutation } from "react-query";
import { hasPermission } from "../../utils/permissions";

const Login = () => {
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const { setAuth } = useAuth();
  const navigate = useNavigate();
  const { showError } = useError();

  const validateForm = () => {
    const newErrors = {};
    if (!email) {
      newErrors.email = "Email is required";
    }

    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const { mutate: loginMutate, isLoading: isLoadingLogin } = useMutation({
    mutationKey: "login",
    mutationFn: () => login(email, password),
    onSuccess: (data) => {
      if (data?.data?.data?.tokens === null) {
        setAuth({
          user: data.data.data.user,
          roles: data?.data?.data?.userPermissions,
        });
        navigate("/one-time-password");
      } else {
        setAuth({
          user: data.data.data.user,
          tokens: data.data.data.tokens,
          roles: data?.data?.data?.userPermissions,
        });
        if (
          hasPermission(
            data?.data?.data?.userPermissions,
            "9cf6c30f-4f90-7a12-19cc-52fcd5bb7c37",
          )
        ) {
          navigate("/dashboard");
        } else {
          navigate("/dashboard/loans-applications");
        }
      }
    },
    onError: (error) => {
      showError(error?.response?.data?.message || error.message);
    },
    retry: 2,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    loginMutate();
  };

  return (
    <div className="h-full flex flex-col pb-[100px] justify-end">
      <p className="text-[#222222] font-semibold text-[24px]">Login</p>
      <p className="text-[#5C5C5C] font-medium text-[13px]">Welcome Back!</p>
      <form className="w-[80%]" onSubmit={handleSubmit} autoComplete="off">
        <div className="flex flex-col gap-[5px] my-[20px]">
          <label
            className="font-medium text-[13px] text-[#000000]"
            htmlFor="email"
          >
            Username
          </label>
          <CustomInput
            placeholder="Enter username"
            type="text"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors((prev) => ({ ...prev, email: "" }));
            }}
          />
          {errors.email && (
            <p className="text-red-500 text-[12px]">{errors.email}</p>
          )}
        </div>
        <div className="flex flex-col gap-[5px] my-[20px]">
          <label
            className="font-medium text-[13px] text-[#000000]"
            htmlFor="password"
          >
            Password
          </label>
          <PasswordInput
            placeholder="Enter your password"
            name="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setErrors((prev) => ({ ...prev, password: "" }));
            }}
          />
          {errors.password && (
            <p className="text-red-500 text-[12px]">{errors.password}</p>
          )}
        </div>
        <div className="flex items-center justify-between mb-[50px]">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-[#2A8C52] focus:ring-[#2A8C52] cursor-pointer transition-all"
              />
              <label
                htmlFor="remember-me"
                className="ml-2 block text-sm text-gray-700 cursor-pointer select-none hover:text-gray-900 transition-colors"
              >
                Remember me
              </label>
            </div>
          </div>
          <Link
            to="/forgot-password"
            className="text-[#000000] underline text-[13px] font-normal"
          >
            Forgot password?
          </Link>
        </div>
        <Button
          label="Login"
          isLoading={isLoadingLogin}
          disabled={isLoadingLogin}
        />
      </form>
    </div>
  );
};

export default Login;
